export type IndustryPresetKey =
  | 'AGRO_LIVESTOCK'
  | 'AGRO_CROPS'
  | 'MANUFACTURING_GENERIC'
  | 'FOOD_PHARMA'
  | 'GENERIC';

export interface TraceableEventTypeOption {
  id: string | number;
  key: string;
  string_value: string;
}

export interface TraceabilityTerminology {
  presetKey: IndustryPresetKey;
  groupLabel: string;
  groupLabelPlural: string;
  groupMembersLabel: string;
  memberUnitLabel: string;
  memberUnitSingleLabel: string;
  batchActionLabel: string;
  batchModalTitle: string;
  batchSaveText: string;
  batchSheetTitle: string;
  batchNotesPlaceholder: string;
  batchSuccessMessage: string;
  metricWeightLabel: string;
  defaultEventTypes: TraceableEventTypeOption[];
}

export const TERMINOLOGY_PRESETS: Record<IndustryPresetKey, TraceabilityTerminology> = {
  AGRO_LIVESTOCK: {
    presetKey: 'AGRO_LIVESTOCK',
    groupLabel: 'Hato / Rebaño',
    groupLabelPlural: 'Hatos / Rebaños',
    groupMembersLabel: 'Miembros del Hato / Rebaño',
    memberUnitLabel: 'Animales',
    memberUnitSingleLabel: 'Animal',
    batchActionLabel: 'Jornada de Pesaje',
    batchModalTitle: 'Jornada de Pesaje Colectivo',
    batchSaveText: 'Guardar Pesajes y Actualizar Hato',
    batchSheetTitle: 'Planilla Rápida de Pesaje por Animal',
    batchNotesPlaceholder: 'Ej. Pesaje Mensual de Ganado',
    batchSuccessMessage: 'Jornada de pesaje registrada y miembros actualizados exitosamente',
    metricWeightLabel: 'Peso (kg)',
    defaultEventTypes: [
      { id: 'VACCINATION', key: 'VACCINATION', string_value: '💉 Vacunación' },
      { id: 'TREATMENT', key: 'TREATMENT', string_value: '💊 Tratamiento / Medicación' },
      { id: 'MEASUREMENT', key: 'MEASUREMENT', string_value: '📏 Medición (peso, alzada, temp.)' },
      { id: 'MAINTENANCE', key: 'MAINTENANCE', string_value: '🔧 Mantenimiento de Instalaciones' },
      { id: 'DEPRECIATION', key: 'DEPRECIATION', string_value: '📉 Depreciación / Desvalorización' },
      { id: 'OBSERVATION', key: 'OBSERVATION', string_value: '👁️ Observación Sanitaria / General' },
    ],
  },
  AGRO_CROPS: {
    presetKey: 'AGRO_CROPS',
    groupLabel: 'Lote / Tablón',
    groupLabelPlural: 'Lotes / Tablones',
    groupMembersLabel: 'Muestras / Plantas del Tablón',
    memberUnitLabel: 'Plantas / Muestras',
    memberUnitSingleLabel: 'Planta / Muestra',
    batchActionLabel: 'Muestreo / Registro Agronómico',
    batchModalTitle: 'Registro de Muestreo Colectivo',
    batchSaveText: 'Guardar Variables Agronómicas',
    batchSheetTitle: 'Planilla Rápida de Muestreo por Unidad',
    batchNotesPlaceholder: 'Ej. Control de Crecimiento y Clorofila',
    batchSuccessMessage: 'Variables de muestreo registradas exitosamente',
    metricWeightLabel: 'Masa / Biomasa (kg/g)',
    defaultEventTypes: [
      { id: 'TREATMENT', key: 'TREATMENT', string_value: '🧪 Fertilización / Fumigación' },
      { id: 'MEASUREMENT', key: 'MEASUREMENT', string_value: '📏 Medición Agronómica (altura, biomasa)' },
      { id: 'MAINTENANCE', key: 'MAINTENANCE', string_value: '🌱 Labores de Cultivo / Poda' },
      { id: 'DEPRECIATION', key: 'DEPRECIATION', string_value: '📉 Merma / Depreciación' },
      { id: 'OBSERVATION', key: 'OBSERVATION', string_value: '👁️ Inspección Fitosanitaria' },
    ],
  },
  MANUFACTURING_GENERIC: {
    presetKey: 'MANUFACTURING_GENERIC',
    groupLabel: 'Lote / Batch de Producción',
    groupLabelPlural: 'Lotes de Fabricación',
    groupMembersLabel: 'Unidades / Piezas del Lote',
    memberUnitLabel: 'Unidades',
    memberUnitSingleLabel: 'Unidad / Pieza',
    batchActionLabel: 'Inspección / Pesaje Masivo',
    batchModalTitle: 'Inspección Colectiva y Control de Calidad',
    batchSaveText: 'Guardar Mediciones del Lote',
    batchSheetTitle: 'Planilla Rápida de Control por Unidad',
    batchNotesPlaceholder: 'Ej. Control Dimensional / Pesaje de Batch',
    batchSuccessMessage: 'Control de calidad y mediciones registradas exitosamente',
    metricWeightLabel: 'Peso / Dimensión (kg)',
    defaultEventTypes: [
      { id: 'MEASUREMENT', key: 'MEASUREMENT', string_value: '📏 Control Dimensional / Pesaje' },
      { id: 'MAINTENANCE', key: 'MAINTENANCE', string_value: '🔧 Mantenimiento / Calibración' },
      { id: 'TREATMENT', key: 'TREATMENT', string_value: '⚙️ Tratamiento Térmico / Acabado' },
      { id: 'DEPRECIATION', key: 'DEPRECIATION', string_value: '📉 Depreciación / Desgaste' },
      { id: 'OBSERVATION', key: 'OBSERVATION', string_value: '👁️ Inspección de Calidad / Observación' },
    ],
  },
  FOOD_PHARMA: {
    presetKey: 'FOOD_PHARMA',
    groupLabel: 'Lote de Fabricación',
    groupLabelPlural: 'Lotes / Batches',
    groupMembersLabel: 'Muestras / Frascos / Envases',
    memberUnitLabel: 'Muestras',
    memberUnitSingleLabel: 'Muestra / Envase',
    batchActionLabel: 'Control Fisicoquímico / Pesaje',
    batchModalTitle: 'Registro de Variables de Calidad',
    batchSaveText: 'Guardar Parámetros de Control',
    batchSheetTitle: 'Planilla de Control de Muestras',
    batchNotesPlaceholder: 'Ej. Ensayo de Estabilidad y Pesaje',
    batchSuccessMessage: 'Parámetros fisicoquímicos registrados exitosamente',
    metricWeightLabel: 'Peso / Masa (kg/g)',
    defaultEventTypes: [
      { id: 'MEASUREMENT', key: 'MEASUREMENT', string_value: '🧪 Control Fisicoquímico (pH, Brix, Masa)' },
      { id: 'MAINTENANCE', key: 'MAINTENANCE', string_value: '🧼 Sanitización / Esterilización CIP' },
      { id: 'TREATMENT', key: 'TREATMENT', string_value: '🔬 Adición de Reactivos / Formulaciones' },
      { id: 'DEPRECIATION', key: 'DEPRECIATION', string_value: '📉 Merma Técnica' },
      { id: 'OBSERVATION', key: 'OBSERVATION', string_value: '👁️ Inspección Sensorial / Visual' },
    ],
  },
  GENERIC: {
    presetKey: 'GENERIC',
    groupLabel: 'Grupo Trazable',
    groupLabelPlural: 'Grupos Trazables',
    groupMembersLabel: 'Miembros del Grupo',
    memberUnitLabel: 'Elementos',
    memberUnitSingleLabel: 'Elemento',
    batchActionLabel: 'Registro Colectivo',
    batchModalTitle: 'Registro Colectivo de Eventos',
    batchSaveText: 'Guardar Registros Colectivos',
    batchSheetTitle: 'Planilla Rápida de Medición',
    batchNotesPlaceholder: 'Ej. Registro de Variables Colectivas',
    batchSuccessMessage: 'Registros colectivos guardados exitosamente',
    metricWeightLabel: 'Medida / Peso (kg)',
    defaultEventTypes: [
      { id: 'MEASUREMENT', key: 'MEASUREMENT', string_value: '📏 Medición de Variables' },
      { id: 'MAINTENANCE', key: 'MAINTENANCE', string_value: '🔧 Mantenimiento / Intervención' },
      { id: 'TREATMENT', key: 'TREATMENT', string_value: '⚙️ Proceso / Tratamiento' },
      { id: 'DEPRECIATION', key: 'DEPRECIATION', string_value: '📉 Depreciación' },
      { id: 'OBSERVATION', key: 'OBSERVATION', string_value: '👁️ Observación General' },
    ],
  },
};

/**
 * Resolves the terminology preset based on cycle type, productive_unit_type or explicit preset string.
 */
export function getTraceabilityTerminology(
  contextKey?: string | null,
  customOverrides?: Partial<TraceabilityTerminology>
): TraceabilityTerminology {
  if (!contextKey) {
    return { ...TERMINOLOGY_PRESETS.AGRO_LIVESTOCK, ...customOverrides };
  }

  const keyUpper = contextKey.toUpperCase().trim();

  let resolvedPreset: TraceabilityTerminology;

  if (
    keyUpper === 'ANIMAL' ||
    keyUpper === 'HERD' ||
    keyUpper === 'LIVESTOCK' ||
    keyUpper === 'AGRO_LIVESTOCK' ||
    keyUpper === 'GANADERIA'
  ) {
    resolvedPreset = TERMINOLOGY_PRESETS.AGRO_LIVESTOCK;
  } else if (
    keyUpper === 'CROP' ||
    keyUpper === 'CROPS' ||
    keyUpper === 'AGRO_CROPS' ||
    keyUpper === 'AGRICOLA' ||
    keyUpper === 'CULTIVO'
  ) {
    resolvedPreset = TERMINOLOGY_PRESETS.AGRO_CROPS;
  } else if (
    keyUpper === 'BATCH' ||
    keyUpper === 'MANUFACTURING' ||
    keyUpper === 'MANUFACTURING_GENERIC' ||
    keyUpper === 'INDUSTRIAL' ||
    keyUpper === 'EQUIPMENT' ||
    keyUpper === 'ASSEMBLY'
  ) {
    resolvedPreset = TERMINOLOGY_PRESETS.MANUFACTURING_GENERIC;
  } else if (
    keyUpper === 'PHARMA' ||
    keyUpper === 'FOOD' ||
    keyUpper === 'FOOD_PHARMA' ||
    keyUpper === 'ALIMENTOS'
  ) {
    resolvedPreset = TERMINOLOGY_PRESETS.FOOD_PHARMA;
  } else if (TERMINOLOGY_PRESETS[keyUpper as IndustryPresetKey]) {
    resolvedPreset = TERMINOLOGY_PRESETS[keyUpper as IndustryPresetKey];
  } else {
    resolvedPreset = TERMINOLOGY_PRESETS.GENERIC;
  }

  return {
    ...resolvedPreset,
    ...customOverrides,
  };
}
